import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLatLngFromUrl } from 'features/mapping/mapPin';
import { HeaderMenuSection } from 'components/PageLayout';
import { useSelectedAlertId } from 'features/alerts/hooks/useSelectedAlertId';
import { useAlertCameraPlayerState } from 'features/cameras/hooks/useAlertCameraPlayerState';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { useSelectedGeoEventId } from 'features/geoEvents/hooks/useSelectedGeoEventId';
import { AnyGeoEvent } from 'features/geoEvents/anyGeoEvent.types';
import { useSelectedCameraId } from 'hooks/useSelectedCameraId';
import { useExternalGeoEvent } from 'hooks/useExternalGeoEvents';
import { useSelectedExternalGeoEventId } from 'features/geoEvents/hooks/useSelectedExternalGeoEventId';
import { useCanEditGeoEvent } from 'features/geoEvents/hooks/useCanEditGeoEvent';
import { isCreateReportDisabled, usePermissions } from 'hooks/usePermissions';
import { useAuthState } from 'state/useAuthState';
import { useMapState, type UseMapState } from 'state/useMapState';
import {
  isFloodingGeoEvent,
  isHazardGeoEvent,
  isLocationGeoEvent,
  isWildfireGeoEvent,
} from 'features/geoEvents/geoEvent.utils';
import { getAdminMenuItems } from './Home.utils';

type GeoEventRouteIds = {
  geoEventId?: number;
  locationId?: number;
  floodingId?: number;
  hazardId?: number;
};

const getGeoEventRouteIds = (geoEvent?: AnyGeoEvent): GeoEventRouteIds => ({
  geoEventId:
    geoEvent && isWildfireGeoEvent(geoEvent) ? geoEvent.id : undefined,
  locationId:
    geoEvent && isLocationGeoEvent(geoEvent) ? geoEvent.id : undefined,
  floodingId:
    geoEvent && isFloodingGeoEvent(geoEvent) ? geoEvent.id : undefined,
  hazardId: geoEvent && isHazardGeoEvent(geoEvent) ? geoEvent.id : undefined,
});

const mergeIncidentMapStateWithUrlParams = (
  incidentMapState: UseMapState['incidentMapState'],
  latitudeParam: number | undefined,
  longitudeParam: number | undefined,
  zoomLevelParam: number | undefined,
): UseMapState['incidentMapState'] => ({
  ...incidentMapState,
  center: {
    ...incidentMapState.center,
    lat: latitudeParam ?? incidentMapState.center.lat,
    lng: longitudeParam ?? incidentMapState.center.lng,
  },
  zoom: zoomLevelParam ?? incidentMapState.zoom,
});

export const useAdminMenuItems = (): HeaderMenuSection[] | undefined => {
  const { t } = useTranslation();
  const {
    permissions: {
      canReportWildfire,
      canReportFlooding,
      canCreateLocation,
      canReportHazard,
      isRxOnlyReporter,
    },
  } = useAuthState();
  const { geoEvent } = useGeoEvent<AnyGeoEvent>({
    id: useSelectedGeoEventId(),
    useCacheBuster: true,
  });
  const selectedExternalGeoEventId = useSelectedExternalGeoEventId();
  const { externalGeoEvent } = useExternalGeoEvent(selectedExternalGeoEventId);
  const { incidentMapState } = useMapState();
  const cameraId = useSelectedCameraId();
  const { timelapseFrame } = useAlertCameraPlayerState();
  const { latitudeParam, longitudeParam, zoomLevelParam } = useLatLngFromUrl();
  const alertId = useSelectedAlertId();

  const { geoEventId, locationId, floodingId, hazardId } =
    getGeoEventRouteIds(geoEvent);
  const { data: permissionsData } = usePermissions({
    geoEventId: geoEvent?.id,
    geoEventType: geoEvent?.geoEventType,
  });
  const isAddUpdateDisabled = geoEvent
    ? isCreateReportDisabled(geoEvent.geoEventType, permissionsData)
    : false;
  const {
    hasEditRole,
    canEditGeoEvent,
    isLoading: isEditPermissionLoading,
  } = useCanEditGeoEvent(geoEvent);
  // Only disable once the permission is known, so it doesn't flash disabled while loading.
  const isEditIncidentDisabled = !isEditPermissionLoading && !canEditGeoEvent;

  const isLocationDetail = geoEvent != null && isLocationGeoEvent(geoEvent);
  const canLocationEditorEditLocation =
    isLocationDetail && hasEditRole && !isEditIncidentDisabled;
  const hasLocationEditorAccess =
    canLocationEditorEditLocation || (canCreateLocation && !locationId);

  const canReport = canReportWildfire || canReportFlooding;
  const showAdminActions =
    (canReport || hasLocationEditorAccess) &&
    ((!geoEventId && !floodingId && !hazardId) || hasEditRole);

  const incidentMapStateForMenu = useMemo(
    () =>
      mergeIncidentMapStateWithUrlParams(
        // When viewing an external geo event, seed coordinates from it so
        // all admin actions land at the event's location, not the map center.
        externalGeoEvent
          ? {
              ...incidentMapState,
              center: {
                ...incidentMapState.center,
                lat: externalGeoEvent.lat,
                lng: externalGeoEvent.lng,
              },
            }
          : incidentMapState,
        latitudeParam,
        longitudeParam,
        zoomLevelParam,
      ),
    [
      incidentMapState,
      externalGeoEvent,
      latitudeParam,
      longitudeParam,
      zoomLevelParam,
    ],
  );

  const adminMenuItems = useMemo(() => {
    return getAdminMenuItems(t, {
      automatedGeoEvent: !geoEvent?.reporterManaged,
      geoEventId,
      locationId,
      floodingId,
      hazardId,
      alertId,
      geoEventMapState: geoEvent
        ? { lat: geoEvent.lat, lng: geoEvent.lng }
        : null,
      canReportWildfire,
      canReportFlooding,
      canCreateLocation,
      canReportHazard,
      incidentMapState: incidentMapStateForMenu,
      timelapseFrame,
      cameraId,
      isRxOnlyReporter,
      isPrescribed:
        geoEvent && isWildfireGeoEvent(geoEvent)
          ? geoEvent.data.isPrescribed
          : false,
      isAddUpdateDisabled,
      isEditIncidentDisabled,
    });
  }, [
    t,
    geoEvent,
    geoEventId,
    locationId,
    floodingId,
    hazardId,
    alertId,
    canReportWildfire,
    canReportFlooding,
    canCreateLocation,
    canReportHazard,
    incidentMapStateForMenu,
    timelapseFrame,
    cameraId,
    isRxOnlyReporter,
    isAddUpdateDisabled,
    isEditIncidentDisabled,
  ]);

  return showAdminActions ? adminMenuItems : undefined;
};
